/**
 * getPrimaryColour
 * ----------------
 * Returns the app's current primary color from CSS variables, with optional
 * format and opacity control.
 *
 * @param {Object} [options]
 * @param {"hex"|"rgb"|"rgba"} [options.format="hex"] - Output format.
 * @param {number} [options.opacity=1] - Opacity (0–1). Returns rgba when < 1.
 *
 * Example usage:
 *   getPrimaryColour()
 *   getPrimaryColour({ format: "rgb" })
 *   getPrimaryColour({ format: "rgb", opacity: 0.3 })
 *   getPrimaryColour({ format: "rgba", opacity: 0.3 })
 */
import colours from "../theme/colours";

const FALLBACK_PRIMARY = colours.primary;

function clampOpacity(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 1;
  return Math.min(1, Math.max(0, n));
}

function normalizeRgbPart(value) {
  const n = Number.parseFloat(String(value).trim());
  if (!Number.isFinite(n)) return null;
  return Math.min(255, Math.max(0, Math.round(n)));
}

function rgbToHex({ r, g, b }) {
  return `#${[r, g, b]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("")}`;
}

function parseHexColour(value) {
  const raw = String(value || "").trim();
  if (!raw.startsWith("#")) return null;

  const hex = raw.slice(1);
  if (!/^[0-9a-fA-F]{3}$/.test(hex) && !/^[0-9a-fA-F]{6}$/.test(hex)) {
    return null;
  }

  const fullHex =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;

  const n = Number.parseInt(fullHex, 16);
  return {
    r: (n >> 16) & 255,
    g: (n >> 8) & 255,
    b: n & 255,
  };
}

function parseRgbColour(value) {
  const match = String(value || "")
    .trim()
    .match(/^rgba?\(([^)]+)\)$/i);

  if (!match) return null;

  const rawParts = match[1].trim();
  const parts = rawParts.includes(",")
    ? rawParts.split(",").map((part) => part.trim())
    : rawParts.split(/[\s/]+/).filter(Boolean);
  if (parts.length < 3) return null;

  const r = normalizeRgbPart(parts[0]);
  const g = normalizeRgbPart(parts[1]);
  const b = normalizeRgbPart(parts[2]);

  if (r == null || g == null || b == null) return null;

  return { r, g, b };
}

function parseColour(value) {
  return parseHexColour(value) || parseRgbColour(value);
}

export function getPrimaryColour({ format = "hex", opacity = 1 } = {}) {
  const alpha = clampOpacity(opacity);

  if (typeof window === "undefined") {
    const fallbackRgb = parseColour(FALLBACK_PRIMARY);
    if (format === "rgb" || format === "rgba" || alpha < 1) {
      return alpha < 1 || format === "rgba"
        ? `rgba(${fallbackRgb.r}, ${fallbackRgb.g}, ${fallbackRgb.b}, ${alpha})`
        : `rgb(${fallbackRgb.r}, ${fallbackRgb.g}, ${fallbackRgb.b})`;
    }
    return FALLBACK_PRIMARY;
  }

  const rootStyle = getComputedStyle(document.documentElement);
  const colorVar =
    rootStyle.getPropertyValue("--color-primary") ||
    rootStyle.getPropertyValue("--tw-color-primary") ||
    FALLBACK_PRIMARY;

  const color = colorVar.trim() || FALLBACK_PRIMARY;
  const rgb = parseColour(color) || parseColour(FALLBACK_PRIMARY);

  if (format === "rgb" || format === "rgba" || alpha < 1) {
    if (alpha < 1 || format === "rgba") {
      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    }
    return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
  }

  return color.startsWith("#") ? color : rgbToHex(rgb);
}
